import { useEffect, useState } from "react";
import classes from "./PaymentMethods.module.scss";
import braintree, { PayPal, type Client } from "braintree-web";
import { trackError } from "@/utils/error-monitoring";
import { useDonationFormStore } from "@/stores";
import { getAsset } from "@/utils/assets";
import { getCountryName } from "@/utils/countries";
import { CURRENCY } from "@/config";

export type PayPalButtonProps = {
  amount: number;
  braintreeClient: Client | null;
  onSuccess: () => void;
};

export function PayPalButton(props: PayPalButtonProps) {
  const { amount, braintreeClient, onSuccess } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [payPalInstance, setPayPalInstance] = useState<PayPal>();
  const { setNonce, setAddress, setIsManualAddress, setPersonalDetails } =
    useDonationFormStore();

  useEffect(() => {
    if (!braintreeClient) return;

    const initializePayPalPay = async () => {
      try {
        const payPalInstance = await braintree.paypal.create({
          client: braintreeClient,
        });
        setPayPalInstance(payPalInstance);
      } catch (error) {
        trackError(error as Error);
      } finally {
        setIsLoading(false);
      }
    };

    initializePayPalPay();
  }, [braintreeClient]);

  const handlePayPalPayment = async () => {
    if (!payPalInstance) return;

    try {
      const payload = await payPalInstance.tokenize({
        flow: "checkout",
        amount: amount,
        currency: CURRENCY,
        enableShippingAddress: true,
      });

      if (!payload.nonce) {
        throw new Error("No nonce found");
      }

      setNonce(payload.nonce);
      setAddress({
        "address-line-1": payload.details.billingAddress?.line1 || "",
        "address-line-2": payload.details.billingAddress?.line2 || "",
        city: payload.details.billingAddress?.city || "",
        region: payload.details.billingAddress?.state || "",
        "postal-code": payload.details.billingAddress?.postalCode || "",
        country: getCountryName(payload.details.billingAddress?.countryCode),
      });

      setPersonalDetails({
        title: "",
        firstName: payload.details.firstName || "",
        lastName: payload.details.lastName || "",
        email: payload.details.email || "",
        confirmEmail: payload.details.email || "",
      });

      setIsManualAddress(true);
      onSuccess();
    } catch (error) {
      if ((error as { code?: string })?.code === "PAYPAL_POPUP_CLOSED") return;

      trackError(error as Error);
    }
  };

  return (
    <button
      type="button"
      className={classes.button}
      onClick={handlePayPalPayment}
      disabled={isLoading}
    >
      <span>Donate with</span>
      <img
        src={getAsset("/images/paypal.svg")}
        className={classes.icon}
        width={907}
        height={245}
        alt="PayPal"
      />
    </button>
  );
}
